<template>
  <div class="share-reading-fee-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('shareReadingFee.page.title')" :subtitle="$t('shareReadingFee.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.roomName" clearable class="w-full"
            :placeholder="$t('shareReadingFee.search.roomName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.ownerNameLike" clearable class="w-full"
            :placeholder="$t('shareReadingFee.search.ownerName')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('shareReadingFee.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('shareReadingFee.page.empty')" @retry="getList">
        <el-table-column prop="roomName" :label="$t('shareReadingFee.table.roomName')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.roomName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="ownerName" :label="$t('shareReadingFee.table.ownerName')" align="center" width="130"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.ownerName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('shareReadingFee.table.feeName')" align="left" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="degrees" :label="$t('shareReadingFee.table.degrees')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.degrees) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="amount" :label="$t('shareReadingFee.table.amount')" align="right" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('shareReadingFee.table.remark')" align="left" min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="120" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleDetail(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('shareReadingFee.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>
  </div>
</template>

<script>
import { listFloorShareFee } from '@/api/fee/shareReadingFeeApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney, formatNumber } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ShareReadingFeeList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        readingId: this.$route.query.readingId || '',
        roomName: '',
        ownerNameLike: '',
        communityId: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.searchForm
        }
        const { data, total } = await listFloorShareFee(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取公摊费用失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('shareReadingFee.page.loadFailed')
        this.$message.error(this.$t('shareReadingFee.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.roomName = ''
      this.searchForm.ownerNameLike = ''
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleDetail(row) {
      this.$router.push({
        path: '/views/fee/feeDetail',
        query: { feeId: row.feeId }
      })
    },
    goBack() {
      this.$router.go(-1)
    },
    formatNumber(value) {
      return formatNumber(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.share-reading-fee-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}
</style>
